import { useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { subName } from "../lib/i18n";

const tmpV = new THREE.Vector3();
const tmpC = new THREE.Vector3();
const GAP = 24;

const spread = (list) => {
  list.sort((a, b) => a.y - b.y);
  list.forEach((v, i) => { v.ly = i ? Math.max(v.y, list[i - 1].ly + GAP) : v.y; });
};

// Inside Canvas: projects anchors and lays out labels in left/right columns (direct DOM writes)
export const CalloutProjector = ({ items, refs }) => {
  useFrame(({ camera, size }) => {
    const vis = [];
    items.forEach((it) => {
      const el = { label: refs.current.labels?.[it.id], path: refs.current.paths?.[it.id] };
      if (!el.label || !el.path) return;
      tmpV.set(...it.pos);
      const facing = !it.normal || it.normal.dot(tmpC.copy(camera.position).sub(tmpV)) > 0;
      tmpV.project(camera);
      if (!facing || tmpV.z > 1) { el.label.style.display = "none"; el.path.setAttribute("d", ""); return; }
      vis.push({ it, el, x: ((tmpV.x + 1) / 2) * size.width, y: ((1 - tmpV.y) / 2) * size.height });
    });
    if (!vis.length) return;
    const xs = vis.map((v) => v.x);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const left = vis.filter((v) => v.x < cx || (v.x === cx && v.it.organ));
    const right = vis.filter((v) => !left.includes(v));
    const pad = Math.min(size.width * 0.18, 150);
    const lx = Math.min(...xs) - pad, rx = Math.max(...xs) + pad;
    spread(left);
    spread(right);
    vis.forEach((v) => {
      const isLeft = left.includes(v);
      const w = v.el.label.offsetWidth;
      const col = isLeft ? lx : rx;
      const x0 = isLeft ? Math.max(6, col - w) : Math.min(col, size.width - w - 6);
      v.el.label.style.display = "block";
      v.el.label.style.transform = `translate(${x0}px, ${v.ly - 26}px)`;
      const inner = isLeft ? x0 + w : x0;
      v.el.path.setAttribute("d", `M${isLeft ? x0 : x0 + w},${v.ly} L${inner},${v.ly} L${v.x},${v.y}`);
    });
  });
  return null;
};

// DOM overlay with labels + leader lines
export const CalloutOverlay = ({ items, refs, lang, subzone, setSubzone }) => (
  <div className="pointer-events-none absolute inset-0 z-[5] overflow-hidden" data-testid="callouts">
    <svg className="absolute inset-0 h-full w-full">
      {items.map((it) => (
        <path key={it.id} ref={(p) => { (refs.current.paths ||= {})[it.id] = p; }}
          fill="none" stroke={subzone === it.id ? "#ea580c" : "rgba(255,255,255,0.95)"} strokeWidth={subzone === it.id ? 2 : 1.3}
          style={{ filter: "drop-shadow(0 0 1.5px rgba(15,23,42,0.55))" }} />
      ))}
    </svg>
    {items.map((it) => (
      <button key={it.id} data-testid={`${it.organ ? "organ" : "hotspot"}-${it.id}`} onClick={() => setSubzone(it.id)}
        ref={(b) => { (refs.current.labels ||= {})[it.id] = b; }}
        className={`callout pointer-events-auto absolute left-0 top-0 whitespace-nowrap px-1 pb-1 font-serif-callout transition-[font-size,color] duration-200 ${subzone === it.id ? "text-[17px] sm:text-[22px] font-semibold text-orange-600" : `text-[12px] sm:text-[17px] ${it.organ ? "text-rose-900" : "text-slate-800"}`}`}
        style={{ display: "none" }}>
        {subName(lang, it.id)}
      </button>
    ))}
  </div>
);

export const useCalloutRefs = () => useRef({});
